import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Link,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useContext, useState } from "react";
import {
  capitalizeEntity,
  convertESTtoUserLocalTime,
  getFormattedStatsCount,
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import SupportIcon from "assets/icons1/help_icon.svg";
import accountUsageStyles from "./AccountUsage.styles";
import { MyPlanContext } from "../Context";
import { planLimits } from "models/interfaces";
import ConfirmationModal from "screens/Templates/Components/ConfirmationModal";
import { cancelSubscription } from "../PlanService";

interface CustomProps {
  isLoading: boolean;
  setIsLoading: Function;
  planCanceled: boolean;
  setPlanCanceled: Function;
}

export const features = [
  // {
  //   title: "Email",
  //   items: [
  //     "Drag and drop email editor",
  //     "Scheduled email sends",
  //     "Spam detection",
  //     "Subject Line Generation",
  //     "Templates",
  //     "Remove Mailzzy Branding",
  //     "A/B Testing",
  //   ],
  // },
  {
    title: "Counts",
    items: ["Contacts", "Monthly email sends", "Custom user permissions"],
  },
  // {
  //   title: "Marketing CRM",
  //   items: ["Tags", "Mailzzy Mobile App", "Custom date range reporting"],
  // },
  {
    title: "Social Marketing",
    items: ["Post on Facebook and Instagram", "Schedule posts in advance"],
  },
  {
    title: "Support",
    items: ["Guides and Tutorials", "Email Support"],
  },
];

const AccountUsage = (props: CustomProps) => {
  const theme = useTheme();
  const classes = accountUsageStyles;
  const { setIsLoading, setPlanCanceled } = props;
  const { featurePlanDetails, accountPlan, isActivePlan } =
    useContext(MyPlanContext);
  const [open, setOpen] = useState<boolean>(false);

  const cancelSubscriptionHandler = async () => {
    try {
      setIsLoading(true);
      setOpen(false);
      const res = await cancelSubscription();
      openSuccessNotification(res.message);
      setPlanCanceled(true);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getLimitsCardTitle = (value: string) => {
    if (value === "Contacts Used") {
      return "Contact Used";
    } else if (value === "Daily Emails") {
      return "Daily Email";
    } else if (value === "Monthly Emails") {
      return "Monthly Email";
    } else {
      return value;
    }
  };

  const getLimitsCard = (title: string, item: any) => {
    const percentage = (
      (parseInt(item.current ?? 0) /
        parseInt(item.limit === "0" ? "1" : item.limit)) *
      100
    ).toFixed();

    return (
      <Card sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={2} justifyContent={"space-between"}>
          <Box>
            <Typography variant="h6" color="text.primary">
              {title}
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={400}
              color="text.primary"
              pt={1}
            >
              {`${getFormattedStatsCount(item.limit ?? 0)} ${capitalizeEntity(
                item.entity
              )}(s)`}
            </Typography>

            <Typography
              variant="subtitle2"
              fontWeight={700}
              color={theme.palette.primary.main}
              pt={2}
            >
              {getFormattedStatsCount(item.current ?? 0).length >= 2
                ? `${getFormattedStatsCount(item.current ?? 0)} ${title}`
                : `${getFormattedStatsCount(
                    item.current ?? 0
                  )} ${getLimitsCardTitle(title)}`}

              {/* {`${getFormattedStatsCount(item.current ?? 0)} ${
                title.toLowerCase().includes("used") ? title : `${title} Used`
              }`} */}
            </Typography>
          </Box>
          <Stack justifyContent="center" alignItems="center">
            <Box position="relative" display="inline-flex" mr={2}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={80}
                sx={{
                  position: "absolute",
                  color: "#EBEBEB",
                }}
              />
              <CircularProgress
                variant="determinate"
                value={+percentage}
                size={80}
                sx={{
                  color: +percentage >= 50 ? "#E71B00" : "#91CD26",
                }}
              />
              <Box sx={classes.circularProgressPercentageBox}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={+percentage >= 50 ? "#E71B00" : "#91CD26"}
                >
                  {percentage}%
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Card>
    );
  };

  const getEntityTitle = (entity: string) => {
    if (entity === "email") {
      return "Monthly Emails";
    } else if (entity === "contact") {
      return "Contacts Used";
    } else if (entity === "dailyEmail") {
      return "Daily Emails";
    } else {
      return entity;
    }
  };

  const getProfileDetails = () => {
    return (
      <Grid container spacing={2}>
        {accountPlan?.appPlanLimitList
          ?.filter(
            (item: any) => item.entity !== "entity" && item.entity !== "sms"
          )
          ?.sort((a: any, b: any) => a.entity.localeCompare(b.entity))
          ?.map((item: any, index: number) => (
            <Grid item xs={12} md={4} key={index}>
              {getLimitsCard(getEntityTitle(item.entity), item)}
            </Grid>
          ))}
      </Grid>
    );
  };

  const getMyPlanDetails = () => {
    return (
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} sm={12}>
          {isTruthy(featurePlanDetails) && (
            <Card>
              <Box sx={classes.planPriceBox}>
                <Stack direction="row" spacing={1}>
                  <Stack direction="row">
                    <Typography variant="caption" sx={classes.planPrice}>
                      {featurePlanDetails.currency === "INR"
                        ? `₹${getFormattedStatsCount(
                            featurePlanDetails?.price ?? 0
                          )}`
                        : `$${getFormattedStatsCount(
                            featurePlanDetails?.price ?? 0
                          )}`}
                    </Typography>
                  </Stack>
                  <Typography sx={classes.planPriceText} variant="caption">
                    / month
                  </Typography>
                </Stack>

                <Typography
                  variant="body1"
                  color={theme.palette.primary.main}
                  fontWeight={700}
                >
                  {`Plan ends on ${convertESTtoUserLocalTime(
                    accountPlan.validTill,
                    "YYYY-MM-DDTHH:mm:s",
                    "Do MMM, YYYY"
                  )}`}
                </Typography>
              </Box>
              <Grid item xs={12}>
                <Stack sx={classes.featureNameStack}>
                  <Grid container>
                    <Grid item xs={12} sm={3} display="flex">
                      <Stack width="100%">
                        <Stack p={2.5} spacing={2}>
                          <Typography variant="h4">
                            {featurePlanDetails.name}
                          </Typography>
                        </Stack>
                        <Divider
                          sx={{
                            margin: "0px !important",
                            width: "100%",
                          }}
                          flexItem
                        />
                        <Stack mt={0} p={2.5}>
                          {featurePlanDetails?.limits.map(
                            (item: planLimits) => (
                              <ListItem
                                sx={{
                                  display: "list-item",
                                  p: "8px !important",
                                }}
                              >
                                {`${getFormattedStatsCount(item.limit ?? 0)} ${
                                  item.entity === "email"
                                    ? "Emails / Month"
                                    : capitalizeEntity(`${item.entity}s`)
                                }`}
                              </ListItem>
                            )
                          )}
                        </Stack>

                        {isActivePlan && featurePlanDetails.price !== 0 && (
                          <Stack mt={0} p={2.5}>
                            <Button
                              variant="contained"
                              onClick={() => setOpen(true)}
                            >
                              Cancel My Subscription
                            </Button>
                          </Stack>
                        )}
                      </Stack>

                      <Divider
                        orientation="vertical"
                        sx={{
                          height: "100%",
                        }}
                        flexItem
                      />
                    </Grid>

                    <Grid item xs={9} sx={classes.featuresMainGridItem}>
                      {isTruthy(features) && (
                        <Grid container spacing={4}>
                          {features.map((category, index) => {
                            if (category.title === "Email") {
                              return (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, marginBottom: 1 }}
                                  >
                                    {category.title}
                                  </Typography>
                                  {category.items.map((item, i) => (
                                    <Typography
                                      key={i}
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        marginBottom: 0.5,
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  ))}
                                </Grid>
                              );
                            }
                            return null;
                          })}

                          <Grid item xs={12} sm={6} md={4}>
                            {features
                              .filter(
                                (category) =>
                                  category.title === "Marketing CRM" ||
                                  category.title === "Counts"
                              )
                              .map((category, index) => (
                                <Box
                                  key={index}
                                  style={{ marginBottom: "16px" }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, marginBottom: 1 }}
                                  >
                                    {category.title}
                                  </Typography>
                                  {category.items.map((item, i) => (
                                    <Typography
                                      key={i}
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        marginBottom: 0.5,
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  ))}
                                </Box>
                              ))}
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            {features
                              .filter(
                                (category) =>
                                  category.title === "Social Marketing" ||
                                  category.title === "Support"
                              )
                              .map((category, index) => (
                                <Box
                                  key={index}
                                  style={{ marginBottom: "16px" }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, marginBottom: 1 }}
                                  >
                                    {category.title}
                                  </Typography>
                                  {category.items.map((item, i) => (
                                    <Typography
                                      key={i}
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        marginBottom: 0.5,
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  ))}
                                </Box>
                              ))}
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            </Card>
          )}
        </Grid>
      </Grid>
    );
  };

  const getHelpCenter = () => {
    return (
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} sm={12}>
          <Card sx={{ p: 2.5 }}>
            <Stack direction="column">
              <Stack direction="column" spacing={2}>
                <Stack
                  alignItems="center"
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  mt={2}
                  spacing={2}
                >
                  <Stack alignItems="center" direction="row" spacing={2}>
                    <img src={SupportIcon} width={"30px"} />
                    <Stack direction="column" spacing={0.5}>
                      <Typography variant="body1" fontWeight={500}>
                        Our support team is here to help! 
                      </Typography>
                      <Typography variant="body2">
                        Need help choosing the right plan for your needs? 
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack>
                    <Link>
                      <a href="mailto:support@mailzzy.com">
                        <Button id="send_btn" variant="contained">
                          Contact Support Team
                        </Button>
                      </a>
                    </Link>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const getStatsSkeleton = () => (
    <Grid container spacing={2}>
      {[...Array(3)].map((_, index) => (
        <Grid item xs={12} sm={4} key={index}>
          <Skeleton animation="wave" variant="rounded" height={150} />
        </Grid>
      ))}
    </Grid>
  );

  const getMyPlanDetailsSkeleton = () => (
    <Grid container spacing={2} mt={2}>
      <Grid item xs={12}>
        <Skeleton animation="wave" variant="rounded" height={80} />
      </Grid>
    </Grid>
  );

  const getHelpCenterSkeleton = () => (
    <Grid container spacing={2} mt={2}>
      {[...Array(1)].map((_, index) => (
        <Grid item xs={12} sm={12} key={index}>
          <Skeleton animation="wave" variant="rounded" height={80} />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      {props.isLoading ? getStatsSkeleton() : getProfileDetails()}
      {props.isLoading ? getMyPlanDetailsSkeleton() : getMyPlanDetails()}
      {props.isLoading ? getHelpCenterSkeleton() : getHelpCenter()}
      {open && (
        <ConfirmationModal
          title={"Confirm cancellation"}
          isVisible={open}
          subtitle2={
            <>
              {`${"Are you sure you want to cancel your current "}`}
              <span style={{ fontWeight: 700 }}>
                {featurePlanDetails.name}{" "}
              </span>
              subscription?
            </>
          }
          btn1Title="Cancel"
          btn1OnClick={() => setOpen(false)}
          btn2Title="Yes"
          btn2OnClick={() => cancelSubscriptionHandler()}
          handleDialogClose={() => setOpen(false)}
          persistOnClickOutside
        />
      )}
    </>
  );
};

export default AccountUsage;
